/** @format */

import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { ApiServiceService } from "src/app/Services/api-service.service";
import { ErrorDialogComponent } from "src/app/Shared/shared/error-dialog/error-dialog.component";
import * as alertify from "alertifyjs";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";

interface SavePayload {
	userId: any;
	lgaId: any;
	areaId: any;
	address: any;
	assessmentType: any;
	presumptiveSubType: any;
	categoryId: any;
	amount: any;
	renewalAmount: any;
	buildingTypeEnum?: string;
}

@Component({
	selector: "app-profiling-user",
	templateUrl: "./profiling-user.component.html",
	styleUrls: ["./profiling-user.component.css"],
})
export class ProfilingUserComponent implements OnInit {
	@Input() user: any;
	@Input() title = "New Profiling";
	@Input() appprovalStatus = null;
	@Input() assessmentId: any;
	@Output() onConfirmClicked = new EventEmitter();

	pendingRequests = 0;

	form!: FormGroup;
	profilingForm!: FormGroup;

	lgas: any;
	areas: any;
	incomeTypes: any;
	isLoading = false;
	isSaving = false;
	classification: any;
	profiles: any;
	businessPremisesIncomeTypes: any;
	fireServiceTypes: any;
	buildingTypes: any;
	buildingTypeSubs: any;
	buildingTypeSubsTypes: any;
	educationCategories: any;
	cultureCategories: any;
	healthCategories: any;
	healthFacilityTypes: any;
	healthCapacities: any;
	aplucCategories: any[] = [];
	aplucTypes: any[] = [];
	aplucRates: any[] = [];
	ansaaCategories: any[] = [];
	licenseCategories: any[] = [];
	amount = 0;
	renewalAmount = 0;

	readonly profilingTypes = [
		{ value: "ASWAMA", label: "ASWAMA" },
		{ value: "BUSINESS_PREMISES", label: "Business Premises" },
		{ value: "FIRE_SERVICE", label: "Fire Service" },
		{ value: "LAND_USE_CHARGE", label: "Land Use Charge" },
		{ value: "PRESUMPTIVE_TAX", label: "Presumptive Tax" },
		{ value: "APLUC", label: "Land Use Charge/APLUC" },
		{ value: "TOURISM", label: "Ministry of Culture and Tourism" },
		{ value: "HEALTH", label: "Ministry of Health" },
		{ value: "EDUCATION", label: "Ministry of Education" },
		{ value: "LICENSE", label: "Licenses" },
		{ value: "ANSAA", label: "ANSAA" },
	];

	readonly businessCategories = [
		{ value: "SMALL", label: "Small Business" },
		{ value: "MEDIUM", label: "Medium Business" },
		{ value: "MICRO", label: "Micro Business" },
	];

	constructor(
		public modalRef: BsModalRef,
		private fb: FormBuilder,
		private apiService: ApiServiceService,
		private bsModalService: BsModalService,
	) {}

	ngOnInit(): void {
		this.getAllIncomeTypes();

		this.form = this.fb.group({
			lgaId: [
				{ value: "", readonly: this.user?.lgaId !== undefined },
				[Validators.required],
			],
			areaId: [
				{ value: "", readonly: this.user?.areaId !== undefined },
				[Validators.required],
			],
			address: [
				{ value: "", readonly: this.user?.address !== undefined },
				[Validators.required],
			],
		});

		this.initializeProfilingForm();
		this.onLoad();
		this.getAllBuildingTypes();
	}

	initializeProfilingForm(): void {
		this.profilingForm = this.fb.group({
			profilingType: ["", Validators.required],
			presumptiveIncomeType: [""],
			businessPremisesTypes: [""],
			fireServiceType: [""],
			buildingType: [""],
			buildingTypeSub: [""],
			buildingTypeSubType: [""],
			category: [""],
			amount: ["", Validators.required],
			renewalAmount: [""],
			buildingTypeEnum: [""],
			healthCategoryId: [""],
			healthFacilityTypeId: [""],
			healthCapacityId: [""],
			cultureCategoryId: [""],
			educationCategoryId: [""],
			aplucCategoryId: [""],
			aplucTypeId: [""],
			aplucRateId: [""],
			ansaaCategoryId: [""],
			licenseCategoryId: [""],
		});

		this.amount = 0;
		this.renewalAmount = 0;
	}

	onProfilingType(): void {
		this.amount = 0;
		const type = this.profilingForm.get("profilingType")!.value;

		const healthControls = [
			"healthCategoryId",
			"healthFacilityTypeId",
			"healthCapacityId",
		] as const;
		const cultureControls = ["cultureCategoryId"] as const;
		const educationControls = ["educationCategoryId"] as const;
		const aplucControls = [
			"aplucCategoryId",
			"aplucTypeId",
			"aplucRateId",
		] as const;
		const ansaaLicenseControls = [
			"ansaaCategoryId",
			"licenseCategoryId",
		] as const;

		if (type === "PRESUMPTIVE_TAX") {
			this.activateControl("presumptiveIncomeType");
			this.activateControl("category");
			this.activateControl("amount");
			this.disableControls(
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...educationControls,
				...aplucControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "BUSINESS_PREMISES") {
			this.getAllBusinessPremisesType();
			this.activateControl("businessPremisesTypes");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...educationControls,
				...aplucControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "FIRE_SERVICE") {
			this.getFireServiceTypes();
			this.activateControl("fireServiceType");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...educationControls,
				...aplucControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "ASWAMA") {
			this.activateControl("buildingTypeEnum");
			this.activateControl("buildingType");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				...healthControls,
				...cultureControls,
				...educationControls,
				...aplucControls,
				"ansaaCategoryId",
				"licenseCategoryId",
			);
		}

		if (type === "EDUCATION") {
			this.getAllEducationCategories();
			this.activateControl("educationCategoryId");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...aplucControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "TOURISM") {
			this.getAllCultureCategories();
			this.activateControl("cultureCategoryId");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...educationControls,
				...aplucControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "HEALTH") {
			this.getAllHealthCategories();
			this.activateControl("healthCategoryId");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				"healthFacilityTypeId",
				"healthCapacityId",
				...cultureControls,
				...educationControls,
				...aplucControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "APLUC") {
			this.getAplucCategories();
			this.activateControl("aplucCategoryId");
			this.disableControls(
				"aplucTypeId",
				"aplucRateId",
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...educationControls,
				...ansaaLicenseControls,
			);
		}

		if (type === "ANSAA") {
			this.getAllAnsaaCategories();
			this.activateControl("ansaaCategoryId");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...educationControls,
				...aplucControls,
				"licenseCategoryId",
			);
		}

		if (type === "LICENSE") {
			this.getAllLicenseCategories();
			this.activateControl("licenseCategoryId");
			this.activateControl("amount");
			this.disableControls(
				"presumptiveIncomeType",
				"category",
				"businessPremisesTypes",
				"fireServiceType",
				"buildingType",
				"buildingTypeSub",
				"buildingTypeSubType",
				"buildingTypeEnum",
				...healthControls,
				...cultureControls,
				...educationControls,
				...aplucControls,
				"ansaaCategoryId",
			);
		}

		this.profilingForm.updateValueAndValidity();
	}

	onBuildingTypeChange(): void {
		const buildingType = this.profilingForm.get("buildingType")!.value;
		const profilingType = this.profilingForm.get("profilingType")!.value;
		this.amount = 0;
		this.getAllBuildingTypesSub();

		if (buildingType == 2 && profilingType === "ASWAMA") {
			this.activateControl("buildingTypeSub");
			this.activateControl("amount");
			this.disableControls("buildingTypeSubType");
			this.profilingForm.updateValueAndValidity();
		}
	}

	onBuildingTypeSubChange(): void {
		const buildingType = this.profilingForm.get("buildingType")!.value;
		const profilingType = this.profilingForm.get("profilingType")!.value;
		const selectedValue = this.profilingForm.get("buildingTypeSub")?.value;
		this.amount = 0;

		if (buildingType == 2 && profilingType === "ASWAMA" && selectedValue) {
			this.profilingForm.patchValue({ buildingTypeEnum: "RESIDENTIAL" });
			const match = this.buildingTypeSubs?.find(
				(s: any) => s.id == selectedValue,
			);
			if (match) {
				this.profilingForm.patchValue({ amount: match.amount });
				this.amount = match.amount;
			}
		} else if (buildingType == 1 && profilingType === "ASWAMA") {
			this.getAllBuildingTypesSubTypes();
			this.profilingForm.patchValue({ buildingTypeEnum: "COMMERCIAL" });
			this.activateControl("buildingTypeSubType");
			this.activateControl("amount");
			this.profilingForm.updateValueAndValidity();
		}
	}

	onHealthCategoryChange(): void {
		const healthCategoryId =
			this.profilingForm.get("healthCategoryId")?.value;
		if (healthCategoryId) {
			this.getHealthFacilityTypes(healthCategoryId);
			this.activateControl("healthFacilityTypeId");
			this.disableControls("healthCapacityId");
			this.profilingForm.updateValueAndValidity();
		}
	}

	onHealthFacilityTypeChange(): void {
		const facilityTypeId = this.profilingForm.get(
			"healthFacilityTypeId",
		)?.value;
		if (facilityTypeId) {
			this.getHealthCapacities(facilityTypeId);
			this.activateControl("healthCapacityId");
			this.profilingForm.updateValueAndValidity();
		}
	}

	onBuildingTypeSubTypeChange(): void {
		const selectedValue = this.profilingForm.get(
			"buildingTypeSubType",
		)?.value;
		this.amount = 0;
		const match = this.buildingTypeSubsTypes?.find(
			(s: any) => s.id == selectedValue,
		);
		if (match) {
			this.profilingForm.patchValue({ amount: match.amount });
			this.amount = match.amount;
		}
	}

	onAplucCategoryChange(): void {
		const categoryId = this.profilingForm.get("aplucCategoryId")?.value;
		this.aplucTypes = [];
		this.aplucRates = [];
		this.amount = 0;
		this.renewalAmount = 0;
		this.profilingForm.patchValue({
			aplucTypeId: "",
			aplucRateId: "",
			amount: "",
			renewalAmount: "",
		});
		this.disableControls("aplucTypeId", "aplucRateId");
		if (!categoryId) return;
		this.activateControl("aplucTypeId");
		this.getAplucTypes(categoryId);
	}

	onAplucTypeChange(): void {
		const typeId = this.profilingForm.get("aplucTypeId")?.value;
		this.aplucRates = [];
		this.amount = 0;
		this.profilingForm.patchValue({ aplucRateId: "", amount: "" });
		this.disableControls("aplucRateId");
		if (!typeId) return;
		this.activateControl("aplucRateId");
		this.getAplucRates(typeId);
	}

	onAplucRateChange(): void {
		const rateId = this.profilingForm.get("aplucRateId")?.value;
		const selectedRate = this.aplucRates.find(
			(rate: any) => rate?.id == rateId,
		);
		const selectedAmount =
			selectedRate?.maxRate ??
			selectedRate?.rate ??
			selectedRate?.price ??
			selectedRate?.amountDue ??
			selectedRate?.registrationAmount ??
			0;
		this.amount = Number(selectedAmount) || 0;
		this.profilingForm.patchValue({ amount: selectedAmount });
	}

	onCategoryOrTypeChange(): void {
		this.amount = 0;
		this.renewalAmount = 0;
		const type = this.profilingForm.get("profilingType")!.value;

		if (
			this.profilingForm.get("presumptiveIncomeType")!.value &&
			this.profilingForm.get("category")!.value
		) {
			this.getIncomeClassificationByIncomeTypeAndCategory();
		}

		if (
			type === "BUSINESS_PREMISES" &&
			this.profilingForm.get("businessPremisesTypes")!.value
		) {
			const selectedValue = this.profilingForm.get(
				"businessPremisesTypes",
			)!.value;
			const match = this.businessPremisesIncomeTypes?.find(
				(t: any) => t.id == selectedValue,
			);
			if (match) {
				this.profilingForm.patchValue({
					amount: match.amount,
					renewalAmount: match.renewalAmount,
				});
				this.amount = match.amount;
				this.renewalAmount = match.renewalAmount;
			}
		}

		if (
			type === "FIRE_SERVICE" &&
			this.profilingForm.get("fireServiceType")!.value
		) {
			const selectedValue =
				this.profilingForm.get("fireServiceType")!.value;
			const match = this.fireServiceTypes?.find(
				(t: any) => t.id == selectedValue,
			);
			if (match) {
				this.profilingForm.patchValue({
					amount: match.amount,
					renewalAmount: match.renewalAmount,
				});
				this.amount = match.amount;
				this.renewalAmount = match.renewalAmount;
			}
		}

		if (
			type === "TOURISM" &&
			this.profilingForm.get("cultureCategoryId")!.value
		) {
			const selectedValue =
				this.profilingForm.get("cultureCategoryId")!.value;
			const match = this.cultureCategories?.find(
				(t: any) => t.id == selectedValue,
			);
			if (match) {
				this.profilingForm.patchValue({ amount: match.amount });
				this.amount = match.amount;
			}
		}

		if (
			type === "EDUCATION" &&
			this.profilingForm.get("educationCategoryId")!.value
		) {
			const selectedValue = this.profilingForm.get(
				"educationCategoryId",
			)!.value;
			const match = this.educationCategories?.find(
				(t: any) => t.id == selectedValue,
			);
			if (match) {
				this.profilingForm.patchValue({ amount: match.amount });
				this.amount = match.amount;
			}
		}

		if (
			type === "HEALTH" &&
			this.profilingForm.get("healthCapacityId")!.value
		) {
			const selectedValue =
				this.profilingForm.get("healthCapacityId")!.value;
			const match = this.healthCapacities?.find(
				(t: any) => t.id == selectedValue,
			);
			if (match) {
				this.profilingForm.patchValue({
					amount: match.registrationAmount,
					renewalAmount: match.renewalAmount,
				});
				this.amount = match.registrationAmount;
				this.renewalAmount = match.renewalAmount;
			}
		}

		if (type === "APLUC" && this.profilingForm.get("aplucRateId")!.value) {
			const selectedValue = this.profilingForm.get("aplucRateId")!.value;
			const match = this.aplucRates?.find(
				(rate: any) => rate?.id == selectedValue,
			);
			if (match) {
				this.amount = match.maxRate;
				this.renewalAmount = match.minRate;
			}
		}

		if (
			type === "ANSAA" &&
			this.profilingForm.get("ansaaCategoryId")!.value
		) {
			const category = this.ansaaCategories.find(
				(item: any) =>
					item.id == this.profilingForm.get("ansaaCategoryId")!.value,
			);
			this.amount = Number(
				category?.amount ?? category?.registrationAmount ?? 0,
			);
			this.profilingForm.patchValue({ amount: this.amount });
		}

		if (
			type === "LICENSE" &&
			this.profilingForm.get("licenseCategoryId")!.value
		) {
			const category = this.licenseCategories.find(
				(item: any) =>
					item.id ==
					this.profilingForm.get("licenseCategoryId")!.value,
			);
			this.amount = Number(
				category?.amount ?? category?.registrationAmount ?? 0,
			);
			this.profilingForm.patchValue({ amount: this.amount });
		}
	}

	remove(id: any): void {
		this.track(
			this.apiService.LoginGet(`/auth/profiling/delete/${id}`),
		).subscribe({
			next: () => {
				this.isSaving = false;
				this.isLoading = false;
				this.getAllProfilesByUser();
				this.notify("success", "Deleted successfully");
				this.resubmitIfQueried();
			},
			error: () => {
				this.confirmAfterError("Action was not successful");
				this.notify("error", "Error performing this operation");
				this.isLoading = false;
				this.isSaving = false;
			},
		});
	}

	save(): void {
		this.isLoading = true;
		this.isSaving = true;

		const pojo: SavePayload = {
			userId: this.user?.userId,
			lgaId: this.form.get("lgaId")?.value,
			areaId: this.form.get("areaId")?.value,
			address: this.form.get("address")?.value,
			assessmentType: this.profilingForm.get("profilingType")?.value,
			presumptiveSubType: this.profilingForm.get("presumptiveIncomeType")
				?.value,
			categoryId:
				this.classification?.id ||
				this.profilingForm.get("buildingTypeSubType")?.value ||
				this.profilingForm.get("buildingTypeSub")?.value ||
				this.profilingForm.get("businessPremisesTypes")?.value ||
				this.profilingForm.get("fireServiceType")?.value ||
				this.profilingForm.get("healthCapacityId")?.value ||
				this.profilingForm.get("cultureCategoryId")?.value ||
				this.profilingForm.get("educationCategoryId")?.value ||
				this.profilingForm.get("aplucRateId")?.value ||
				this.profilingForm.get("ansaaCategoryId")?.value ||
				this.profilingForm.get("licenseCategoryId")?.value,
			amount: this.profilingForm.get("amount")?.value,
			renewalAmount: this.profilingForm.get("renewalAmount")?.value,
		};

		const buildingTypeEnum =
			this.profilingForm.get("buildingTypeEnum")?.value;
		if (buildingTypeEnum) {
			pojo.buildingTypeEnum = buildingTypeEnum;
		}

		this.track(
			this.apiService.loginPost_("/auth/profiling/save", pojo),
		).subscribe({
			next: (response) => {
				this.isSaving = false;
				this.isLoading = false;

				if (response.message === "Successful") {
					this.notify("success", "Added successfully");
					this.getAllProfilesByUser();
					this.resubmitIfQueried();
				} else {
					this.confirmAfterError(response.message);
				}
			},
			error: () => {
				this.confirmAfterError("Action was not successful");
				this.notify("error", "Error performing this operation");
				this.isLoading = false;
				this.isSaving = false;
			},
		});
	}

	private resubmitIfQueried(): void {
		if (this.appprovalStatus !== "QUERIED" || !this.assessmentId) {
			return;
		}
		this.track(
			this.apiService.post(
				`/assessments/resubmit/non-tax-assessment/single?assessmentId=${this.assessmentId}`,
				{},
			),
		).subscribe({
			next: (response) => {
				if (
					response?.message === "Assessment Resubmitted Successfully"
				) {
					this.notify("success", "Assessment moved back to pending");
				} else {
					this.confirmAfterError(response?.message);
				}
			},
			error: () => {
				this.notify(
					"error",
					"Unable to move assessment back to pending",
				);
			},
		});
	}

	getAreas(): void {
		const lgaId = this.form.get("lgaId")?.value;
		this.initializeProfilingForm();
		this.track(
			this.apiService.LoginGet(`/auth/area/all/${lgaId}`),
		).subscribe({
			next: (response) => {
				this.areas = response;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	decline(): void {
		this.modalRef.hide();
		window.location.reload();
	}

	private onLoad(): void {
		this.getLgas();
		this.getAreasOnLoad();
		this.getAllProfilesByUser();
		this.form.patchValue({
			lgaId: this.user?.lgaId || "",
			areaId: this.user?.areaId || "",
			address: this.user?.address || "",
		});
	}

	private getLgas(): void {
		this.track(this.apiService.LoginGet("/auth/lga/all")).subscribe({
			next: (response) => {
				this.lgas = response;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAreasOnLoad(): void {
		this.track(
			this.apiService.LoginGet(`/auth/area/all/${this.user?.lgaId}`),
		).subscribe({
			next: (response) => {
				this.areas = response;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllIncomeTypes(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/income_types/all"),
		).subscribe({
			next: (response) => {
				this.incomeTypes = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllBuildingTypes(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/building_types"),
		).subscribe({
			next: (response) => {
				this.buildingTypes = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAplucCategories(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/apluc/categories"),
		).subscribe({
			next: (response) => {
				this.aplucCategories = this.extractOptions(response);
			},
			error: (error) => {
				console.log(error);
				this.aplucCategories = [];
			},
		});
	}

	private getAllAnsaaCategories(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/ansaa_categories"),
		).subscribe({
			next: (response) => {
				this.ansaaCategories = this.extractOptions(response);
			},
			error: (error) => {
				console.log(error);
				this.ansaaCategories = [];
			},
		});
	}

	private getAllLicenseCategories(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/license_categories"),
		).subscribe({
			next: (response) => {
				this.licenseCategories = this.extractOptions(response);
			},
			error: (error) => {
				console.log(error);
				this.licenseCategories = [];
			},
		});
	}

	private getAplucTypes(categoryId: any): void {
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/apluc/types/category/${categoryId}`,
			),
		).subscribe({
			next: (response) => {
				this.aplucTypes = this.extractOptions(response);
			},
			error: (error) => {
				console.log(error);
				this.aplucTypes = [];
			},
		});
	}

	private getAplucRates(typeId: any): void {
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/apluc/rates/type/${typeId}`,
			),
		).subscribe({
			next: (response) => {
				this.aplucRates = this.extractOptions(response);
			},
			error: (error) => {
				console.log(error);
				this.aplucRates = [];
			},
		});
	}

	private extractOptions(response: any): any[] {
		if (Array.isArray(response)) return response;
		if (Array.isArray(response?.data)) return response.data;
		if (Array.isArray(response?.data?.content))
			return response.data.content;
		return [];
	}

	private getAllEducationCategories(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/education_categories"),
		).subscribe({
			next: (response) => {
				this.educationCategories = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllCultureCategories(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/culture_categories"),
		).subscribe({
			next: (response) => {
				this.cultureCategories = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllHealthCategories(): void {
		this.track(
			this.apiService.LoginGet("/auth/profiling/health_categories"),
		).subscribe({
			next: (response) => {
				this.healthCategories = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	getHealthFacilityTypes(healthCategoryId: any): void {
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/facility_types/health_category/${healthCategoryId}`,
			),
		).subscribe({
			next: (response) => {
				this.healthFacilityTypes = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	getHealthCapacities(facilityTypeId: any): void {
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/health_capacities/facility_type/${facilityTypeId}`,
			),
		).subscribe({
			next: (response) => {
				this.healthCapacities = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllBuildingTypesSub(): void {
		const id = this.profilingForm.get("buildingType")!.value;
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/building_sub_types/${id}`,
			),
		).subscribe({
			next: (response) => {
				this.buildingTypeSubs = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllBuildingTypesSubTypes(): void {
		const id = this.profilingForm.get("buildingTypeSub")!.value;
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/building_sub_types_sub/${id}`,
			),
		).subscribe({
			next: (response) => {
				this.buildingTypeSubsTypes = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllProfilesByUser(): void {
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/get_profiles/${this.user.userId}`,
			),
		).subscribe({
			next: (response) => {
				this.profiles = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getAllBusinessPremisesType(): void {
		this.track(
			this.apiService.LoginGet(
				"/auth/profiling/business_premises_types/",
			),
		).subscribe({
			next: (response) => {
				this.businessPremisesIncomeTypes = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getFireServiceTypes(): void {
		const areaId = this.form.get("areaId")?.value;
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/fire_service_types/${areaId}`,
			),
		).subscribe({
			next: (response) => {
				this.fireServiceTypes = response.data;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private getIncomeClassificationByIncomeTypeAndCategory(): void {
		this.amount = 0;
		const incomeType = this.profilingForm.get(
			"presumptiveIncomeType",
		)!.value;
		const category = this.profilingForm.get("category")!.value;
		this.track(
			this.apiService.LoginGet(
				`/auth/profiling/income_classification/${incomeType}/${category}`,
			),
		).subscribe({
			next: (response) => {
				this.classification = response.data;
				this.profilingForm.patchValue({
					amount: this.classification?.price,
				});
				this.amount = this.classification?.price;
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	private track<T>(request: Observable<T>): Observable<T> {
		this.pendingRequests++;
		return request.pipe(finalize(() => this.pendingRequests--));
	}

	private confirmAfterError(message: string): void {
		const dfModalRef = this.bsModalService.show(ErrorDialogComponent, {
			initialState: { body: message },
			class: "modal-sm modal-dialog-centered",
		});
		dfModalRef?.content?.onConfirmClicked.subscribe(() =>
			dfModalRef.hide(),
		);
	}

	private notify(type: "success" | "error", message: string): void {
		alertify.set("notifier", "position", "top-center");
		alertify[type](message);
	}

	private activateControl(name: string): void {
		const ctrl = this.profilingForm.get(name)!;
		ctrl.reset({ value: "", disabled: false }, { emitEvent: false });
		ctrl.setValidators([Validators.required]);
	}

	private disableControls(...names: string[]): void {
		names.forEach((name) =>
			this.profilingForm
				.get(name)!
				.reset({ value: "", disabled: true }, { emitEvent: false }),
		);
	}
}
